import {iChatMessage, iChatSession} from "../../types/internal";

export interface iChatStats {
    unread_count: number
}

export interface iChatMessagesSession {
    session_id: number,
    messages: iChatMessage[]
}


export type ChatState = {
    chat_stats?: iChatStats,
    sessions?: iChatSession[],
    sessions_total?: number,
    sessions_page?: number,
    sessions_per_page?: number,
    active_sessions?: iChatSession[],
    active_total?: number,
    active_page?: number,
    active_per_page?: number,
}

export type iChatAction =
    { type: 'chat_stats', chat_stats: iChatStats }
    | { type: 'sessions', sessions: iChatSession[], total: number, page: number, per_page: number,totalRecords:number }
    | { type: 'active_sessions', sessions: iChatSession[], total: number, page: number, per_page: number }
    | { type: 'replace_message', message: iChatMessage, replace_with_id: number, session: iChatSession }
    | { type: 'message', message: iChatMessage, session: iChatSession }
    | { type: 'messages', messages: iChatMessage[], session: iChatSession }
    | { type: 'session_patch', session_id: number, patch: Partial<iChatSession> }
    ;

function sessionIdMatch(a: { id?: number | string } | undefined, b: number | string | undefined): boolean {
    if (a?.id == null || b == null) {
        return false;
    }
    return a.id.toString() === b.toString();
}

function bumpSessionToFront(list: iChatSession[], sessionId: number | string): iChatSession[] {
    const idx = list.findIndex((s) => sessionIdMatch(s, sessionId));
    if (idx <= 0) {
        return list;
    }
    const next = [...list];
    const [item] = next.splice(idx, 1);
    next.unshift(item);
    return next;
}

function applyIncomingMessage(
    list: iChatSession[] | undefined,
    message: iChatMessage,
    broadcastSession: iChatSession,
): iChatSession[] | undefined {
    if (!list) {
        return list;
    }

    const sessionKey = message.session_id ?? broadcastSession.id;
    let next = [...list];
    let session = next.find((v) => sessionIdMatch(v, sessionKey));

    if (!session) {
        next = [{ ...broadcastSession, messages: broadcastSession.messages ? [...broadcastSession.messages] : [] }, ...next];
        session = next[0];
    }

    if (!session.messages) {
        session.messages = [];
    }

    if (broadcastSession.agent_unread_messages !== undefined) {
        session.agent_unread_messages = broadcastSession.agent_unread_messages;
    }
    session.last_message = broadcastSession.last_message || message;

    const alreadyExists = session.messages.some(
        (value) => value.id != null && message.id != null && value.id.toString() === message.id.toString()
    );
    if (!alreadyExists) {
        session.messages = [...session.messages, message];
    }

    return bumpSessionToFront(next, session.id);
}

function mergeSessionLists(
    incoming: iChatSession[],
    previous: iChatSession[] | undefined,
): iChatSession[] {
    const old = previous ? [...previous] : [];
    const merged: iChatSession[] = [];
    for (let i = 0; i < incoming.length; i++) {
        const existing = old.find((value) => value.id === incoming[i].id);
        if (!existing) {
            merged.push(incoming[i]);
        } else {
            const incomingLast = incoming[i].last_message?.created_at
                ? Date.parse(String(incoming[i].last_message.created_at))
                : 0;
            const existingLast = existing.last_message?.created_at
                ? Date.parse(String(existing.last_message.created_at))
                : 0;
            merged.push({
                ...incoming[i],
                messages: existing.messages,
                // Keep newer preview if pub/sub beat the poll.
                last_message: existingLast > incomingLast ? existing.last_message : incoming[i].last_message,
                agent_unread_messages:
                    existingLast > incomingLast && existing.agent_unread_messages != null
                        ? existing.agent_unread_messages
                        : incoming[i].agent_unread_messages,
            });
        }
    }
    merged.sort((a, b) => {
        if (a.last_message?.created_at && b.last_message?.created_at) {
            return new Date(b.last_message.created_at).getTime() - new Date(a.last_message.created_at).getTime();
        }
        return (b.id || 0) - (a.id || 0);
    });
    return merged;
}

export function chatReducer(state: ChatState, action: iChatAction): ChatState {
    let state_to_updated = {...state};
    switch (action.type) {
        case 'chat_stats':
            return {...state, chat_stats: action.chat_stats};
        case 'message': {
            const message = action.message;
            return {
                ...state,
                sessions: applyIncomingMessage(state.sessions, message, action.session),
                active_sessions: applyIncomingMessage(state.active_sessions, message, action.session),
            };
        }
        case 'messages': {
            const attachMessages = (list: iChatSession[] | undefined): iChatSession[] | undefined => {
                if (!list) {
                    return list;
                }
                let s = [...list];
                let session = s.find((value) => value.id.toString() === action.session.id.toString());
                if (!session) {
                    s = [{ ...action.session, messages: [] }, ...s];
                    session = s[0];
                }
                if (!session.messages) {
                    session.messages = [];
                }
                for (let i = 0; i < action.messages.length; i++) {
                    const exists = session.messages.some(
                        (value) => value.id && action.messages[i].id && value.id.toString() === action.messages[i].id.toString()
                    );
                    if (!exists) {
                        session.messages.push(action.messages[i]);
                    }
                }
                session.messages.sort((a, b) => (a.id && b.id ? a.id - b.id : 0));
                return s;
            };
            return {
                ...state,
                sessions: attachMessages(state.sessions),
                active_sessions: attachMessages(state.active_sessions),
            };
        }
        case 'replace_message':
            if (state.sessions && state.sessions.length > 0) {
                let s = [...state.sessions];
                let session = s.find(value => {
                    return value.id.toString() === action.session.id.toString()
                })
                if (session) {
                    if (!session.messages) {
                        session.messages = []
                    }
                    let __message_already_exist = session.messages.findIndex(value => value.id.toString() === action.message.id.toString()) !== -1;
                    let __replace_message_exist = session.messages.findIndex(value => value.id.toString() === action.replace_with_id.toString()) !== -1;
                    if (!__message_already_exist && __replace_message_exist) {
                        let messages = [...session.messages];

                        let message_index = messages.findIndex(value => value.id === action.replace_with_id);
                        if (message_index !== -1 && action.message) {

                            messages[message_index].message = action.message.message;
                            messages[message_index].id = action.message.id;
                            messages[message_index].uid = action.message.uid;
                            messages[message_index].created_at = action.message.created_at;
                            messages[message_index].updated_at = action.message.updated_at;
                            messages[message_index].attachment_full_url = action.message.attachment_full_url;
                            messages[message_index].attachment_mime_type = action.message.attachment_mime_type;
                            messages[message_index].attachment_size = action.message.attachment_size;
                            messages[message_index].sender_name = action.message.sender_name;
                            messages[message_index].from_guest = action.message.from_guest;
                        }
                        session.messages = messages;
                        session.last_message = action.message;
                        let session_index = state.sessions.findIndex(value => value.id === action.session.id)
                        if (session_index !== -1) {
                            let sessions = state.sessions ? [...state.sessions] : []
                            sessions[session_index] = session
                            state_to_updated = {...state_to_updated, sessions: bumpSessionToFront(sessions, action.session.id)};

                        }

                    }
                }

            }
            if (state.active_sessions && state.active_sessions.length > 0) {
                let s = [...state.active_sessions];
                let session = s.find(value => {
                    return value.id.toString() === action.session.id.toString()
                })
                if (session) {
                    if (!session.messages) {
                        session.messages = []
                    }
                    let __message_already_exist = session.messages.findIndex(value => value.id.toString() === action.message.id.toString()) !== -1;
                    let __replace_message_exist = session.messages.findIndex(value => value.id.toString() === action.replace_with_id.toString()) !== -1;
                    if (!__message_already_exist && __replace_message_exist) {
                        let messages = [...session.messages];

                        let message_index = messages.findIndex(value => value.id === action.replace_with_id);
                        if (message_index !== -1 && action.message) {

                            messages[message_index].message = action.message.message;
                            messages[message_index].id = action.message.id;
                            messages[message_index].uid = action.message.uid;
                            messages[message_index].created_at = action.message.created_at;
                            messages[message_index].updated_at = action.message.updated_at;
                            messages[message_index].attachment_full_url = action.message.attachment_full_url;
                            messages[message_index].attachment_mime_type = action.message.attachment_mime_type;
                            messages[message_index].attachment_size = action.message.attachment_size;
                            messages[message_index].sender_name = action.message.sender_name;
                            messages[message_index].from_guest = action.message.from_guest;
                        }
                        session.messages = messages;
                        session.last_message = action.message;
                        let session_index = state.active_sessions.findIndex(value => value.id === action.session.id)
                        if (session_index !== -1) {
                            let sessions = state.active_sessions ? [...state.active_sessions] : [];
                            sessions[session_index] = session;
                            state_to_updated = {...state_to_updated, active_sessions: bumpSessionToFront(sessions, action.session.id)};

                        }

                    }
                }

            }
            return {...state_to_updated};
        case "sessions":
            return {
                ...state,
                sessions: mergeSessionLists(action.sessions, state.sessions),
                sessions_page: action.page,
                sessions_per_page: action.per_page,
                sessions_total: action.total,
            };
        case "active_sessions":
            return {
                ...state,
                // Was wrongly merging against state.sessions — wiped live message threads.
                active_sessions: mergeSessionLists(action.sessions, state.active_sessions),
                active_page: action.page,
                active_per_page: action.per_page,
                active_total: action.total
            };
        case 'session_patch': {
            const patchSession = (list?: iChatSession[]) => {
                if (!list) {
                    return list;
                }
                const idx = list.findIndex((s) => s.id.toString() === action.session_id.toString());
                if (idx === -1) {
                    return list;
                }
                const next = [...list];
                next[idx] = { ...next[idx], ...action.patch };
                return next;
            };
            return {
                ...state,
                sessions: patchSession(state.sessions),
                active_sessions: patchSession(state.active_sessions),
            };
        }
        default:
            return {...state};
    }
}
